/**
 * IPTV Distributor Management Panel — Wave 2 UI for 3-tier distribution.
 *
 * Renders two different experiences based on the role of the logged-in
 * portal user (auto-detected from the customer_type of the resolved
 * customer record):
 *
 *   - mode="partner"     → Partner sees their Distributors list, can
 *                          create new ones, set per-distributor pricing,
 *                          view each Distributor's wallet + LCO count.
 *   - mode="distributor" → Distributor sees their own profile + wallet,
 *                          can set their OWN ADIGITAL API key (spec 3b),
 *                          create LCOs under themselves, and set per-LCO
 *                          pricing.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Wallet, Users, KeyRound, Pencil, Search, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function useAuth() {
  const token = localStorage.getItem('portal_token');
  return useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
    jsonHeaders: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  }), [token]);
}

/* ============================================================
 * PARTNER MODE — Distributors list + create + set pricing
 * ============================================================ */

function PartnerDistributorsPanel({ packages }) {
  const auth = useAuth();
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingDist, setPricingDist] = useState(null);
  const [pricingRows, setPricingRows] = useState([]);
  const [form, setForm] = useState({
    name: '', company_name: '', email: '', phone: '', address: '', iptv_access_token: '',
  });

  const fetchDistributors = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributors`, { headers: auth.headers });
      if (r.ok) {
        const d = await r.json();
        setDistributors(d.distributors || []);
      } else {
        toast.error((await r.json()).detail || 'Failed to load distributors');
      }
    } catch (e) { toast.error('Network error'); }
    setLoading(false);
  };

  useEffect(() => { fetchDistributors(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return distributors;
    return distributors.filter(d =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.company_name || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q) ||
      (d.phone || '').includes(q)
    );
  }, [distributors, search]);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error('Name, email and phone are required');
      return;
    }
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributors`, {
        method: 'POST', headers: auth.jsonHeaders, body: JSON.stringify(form),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success('Distributor created');
        setShowCreate(false);
        setForm({ name: '', company_name: '', email: '', phone: '', address: '', iptv_access_token: '' });
        fetchDistributors();
      } else {
        toast.error(d.detail || 'Create failed');
      }
    } catch { toast.error('Network error'); }
  };

  const handleDeactivate = async (dist) => {
    if (!window.confirm(`Deactivate distributor "${dist.company_name || dist.name}"?`)) return;
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributors/${dist.customer_id}`, {
        method: 'DELETE', headers: auth.headers,
      });
      const d = await r.json();
      if (r.ok) { toast.success('Deactivated'); fetchDistributors(); }
      else toast.error(d.detail || 'Failed');
    } catch { toast.error('Network error'); }
  };

  const openPricing = async (dist) => {
    setPricingDist(dist);
    setPricingRows([]);         // reset so previous distributor's rows don't leak into this dialog
    setShowPricing(true);
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributor-pricing/${dist.customer_id}`, { headers: auth.headers });
      if (r.ok) {
        const d = await r.json();
        setPricingRows(d.pricing || []);
      }
    } catch (e) { /* non-blocking */ }
  };

  const savePricing = async (package_id, price) => {
    const n = parseFloat(price);
    if (isNaN(n) || n < 0) { toast.error('Enter a valid price'); return; }
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributor-pricing`, {
        method: 'POST', headers: auth.jsonHeaders,
        body: JSON.stringify({ distributor_id: pricingDist.customer_id, package_id, price: n }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Price set: ${n.toFixed(2)}`);
        // Refresh pricingRows
        const r2 = await fetch(`${API}/api/iptv/portal/distributor-pricing/${pricingDist.customer_id}`, { headers: auth.headers });
        if (r2.ok) setPricingRows((await r2.json()).pricing || []);
      } else toast.error(d.detail || 'Failed');
    } catch { toast.error('Network error'); }
  };

  const priceFor = (pkg_id) => pricingRows.find(r => r.package_id === pkg_id)?.price ?? '';

  return (
    <Card data-testid="partner-distributors-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" /> Distributors ({filtered.length}{search ? ` of ${distributors.length}` : ''})
          </CardTitle>
          <div className="flex gap-2">
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-sm" data-testid="distributor-search-input" />
            </div>
            <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-distributor-btn">
              <Plus className="h-4 w-4 mr-1" /> New Distributor
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            No distributors yet. Click <b>New Distributor</b> to create one — they can then create their own LCOs.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Distributor</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>LCOs</TableHead>
                  <TableHead>Wallet</TableHead>
                  <TableHead>Own API Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(d => (
                  <TableRow key={d.customer_id} data-testid={`distributor-row-${d.customer_id}`}>
                    <TableCell>
                      <div className="font-medium">{d.company_name || d.name}</div>
                      <div className="text-xs text-slate-500">{d.name}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{d.email}</div>
                      <div className="text-xs text-slate-500">{d.phone}</div>
                    </TableCell>
                    <TableCell className="text-sm font-mono">{d.lco_count || 0}</TableCell>
                    <TableCell className="text-sm font-mono">₹{(d.wallet_balance || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      {d.iptv_access_token
                        ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Configured</Badge>
                        : <Badge variant="outline" className="text-slate-500">Uses Partner&apos;s</Badge>}
                    </TableCell>
                    <TableCell>
                      {d.is_active === false
                        ? <Badge className="bg-rose-100 text-rose-800 border-rose-300">Inactive</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Active</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openPricing(d)} data-testid={`distributor-pricing-btn-${d.customer_id}`}>
                        <Pencil className="h-4 w-4 mr-1" /> Pricing
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeactivate(d)} disabled={d.is_active === false}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Create Distributor dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Distributor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[
              { k: 'name', l: 'Contact Name *', p: 'Rajesh Kumar' },
              { k: 'company_name', l: 'Company / Trade Name', p: 'ABC Distribution' },
              { k: 'email', l: 'Email *', p: 'rajesh@example.com' },
              { k: 'phone', l: 'Phone *', p: '9899887766' },
              { k: 'address', l: 'Address', p: 'Bangalore, KA' },
              { k: 'iptv_access_token', l: 'Own ADIGITAL API Key (optional)', p: 'leave blank to inherit your Partner key' },
            ].map(f => (
              <div key={f.k}>
                <Label>{f.l}</Label>
                <Input
                  value={form[f.k]}
                  onChange={e => setForm({ ...form, [f.k]: e.target.value })}
                  placeholder={f.p}
                  data-testid={`create-distributor-${f.k}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-create-distributor">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Distributor Pricing dialog */}
      <Dialog open={showPricing} onOpenChange={setShowPricing}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Set Package Prices for {pricingDist?.company_name || pricingDist?.name}</DialogTitle>
            <div className="text-xs text-slate-500 mt-1">
              This is the price the Distributor pays you per subscription. They can add their own markup on top when pricing LCOs.
            </div>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {(packages || []).map(pkg => (
              <DistributorPricingRow
                key={pkg.package_id}
                pkg={pkg}
                current={priceFor(pkg.package_id)}
                onSave={(p) => savePricing(pkg.package_id, p)}
              />
            ))}
            {(!packages || packages.length === 0) && (
              <div className="text-sm text-slate-500 py-4 text-center">No packages available.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPricing(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DistributorPricingRow({ pkg, current, onSave }) {
  const [val, setVal] = useState(current === '' ? '' : String(current));
  useEffect(() => { setVal(current === '' ? '' : String(current)); }, [current]);
  // Prefer the caller's real cost (effective_price for Distributor, else
  // distributor_price, else partner_price) over the raw platform base_price.
  // For a Distributor setting LCO pricing this is Rs.249 (what Partner
  // charges the Distributor), not Rs.250 (platform base). Their commission
  // is computed against this figure, so this is what must be surfaced.
  const cost = (typeof pkg.effective_price === 'number' && pkg.effective_price > 0)
    ? pkg.effective_price
    : (typeof pkg.distributor_price === 'number' && pkg.distributor_price > 0)
      ? pkg.distributor_price
      : (typeof pkg.partner_price === 'number' && pkg.partner_price > 0)
        ? pkg.partner_price
        : pkg.base_price;
  const numericCurrent = parseFloat(val || '0');
  const margin = numericCurrent > 0 && cost > 0 ? (numericCurrent - cost) : null;
  return (
    <div className="flex items-center gap-3 border rounded p-2" data-testid={`distributor-pricing-row-${pkg.package_id}`}>
      <div className="flex-1">
        <div className="font-medium text-sm">{pkg.name}</div>
        <div className="text-xs text-slate-500">
          Your cost ₹{cost?.toFixed(2)} · {pkg.validity_days}d
          {margin !== null && (
            <span className={`ml-2 ${margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              (margin ₹{margin.toFixed(2)})
            </span>
          )}
        </div>
      </div>
      <Input
        type="number" step="0.01" min="0"
        placeholder="Price"
        value={val}
        onChange={e => setVal(e.target.value)}
        className="w-28 h-8 text-sm"
        data-testid={`distributor-price-input-${pkg.package_id}`}
      />
      <Button size="sm" onClick={() => onSave(val)} className="bg-indigo-600 hover:bg-indigo-700 h-8" data-testid={`distributor-price-save-${pkg.package_id}`}>Save</Button>
    </div>
  );
}


/* ============================================================
 * DISTRIBUTOR MODE — my profile, my API key, my LCOs
 * ============================================================ */

function DistributorSelfPanel({ packages }) {
  const auth = useAuth();
  const [me, setMe] = useState(null);
  const [lcos, setLcos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showCreateLco, setShowCreateLco] = useState(false);
  const [lcoForm, setLcoForm] = useState({ name: '', company_name: '', email: '', phone: '', address: '' });
  const [showLcoPricing, setShowLcoPricing] = useState(false);
  const [pricingLco, setPricingLco] = useState(null);
  const [lcoPricingRows, setLcoPricingRows] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/api/iptv/portal/distributor/me`, { headers: auth.headers }),
        fetch(`${API}/api/iptv/portal/distributor/lcos`, { headers: auth.headers }),
      ]);
      if (r1.ok) setMe((await r1.json()).distributor);
      if (r2.ok) setLcos((await r2.json()).lcos || []);
    } catch (e) { /* non-blocking */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const saveApiKey = async () => {
    if (!apiKeyInput.trim()) { toast.error('Enter a valid key'); return; }
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributor/api-key`, {
        method: 'PUT', headers: auth.jsonHeaders,
        body: JSON.stringify({ iptv_access_token: apiKeyInput.trim() }),
      });
      const d = await r.json();
      if (r.ok) { toast.success('API key updated'); setShowApiKey(false); setApiKeyInput(''); load(); }
      else toast.error(d.detail || 'Failed');
    } catch { toast.error('Network error'); }
  };

  const clearApiKey = async () => {
    if (!window.confirm("Clear your own API key? Activations will fall back to your Partner's key.")) return;
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributor/api-key`, {
        method: 'DELETE', headers: auth.headers,
      });
      if (r.ok) { toast.success('Cleared'); load(); }
    } catch { toast.error('Network error'); }
  };

  const createLco = async () => {
    if (!lcoForm.name || !lcoForm.email || !lcoForm.phone) {
      toast.error('Name, email and phone are required');
      return;
    }
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributor/lcos`, {
        method: 'POST', headers: auth.jsonHeaders, body: JSON.stringify(lcoForm),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success('LCO created');
        setShowCreateLco(false);
        setLcoForm({ name: '', company_name: '', email: '', phone: '', address: '' });
        load();
      } else toast.error(d.detail || 'Failed');
    } catch { toast.error('Network error'); }
  };

  const openLcoPricing = async (lco) => {
    setPricingLco(lco);
    setLcoPricingRows([]);          // reset so previous LCO's rows don't leak in
    setShowLcoPricing(true);
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributor/lco-pricing/${lco.customer_id}`,
                            { headers: auth.headers });
      if (r.ok) {
        const d = await r.json();
        setLcoPricingRows(d.pricing || []);
      }
    } catch (e) { /* non-blocking */ }
  };

  const saveLcoPricing = async (package_id, price) => {
    const n = parseFloat(price);
    if (isNaN(n) || n < 0) { toast.error('Enter a valid price'); return; }
    try {
      const r = await fetch(`${API}/api/iptv/portal/distributor/lco-pricing`, {
        method: 'POST', headers: auth.jsonHeaders,
        body: JSON.stringify({ lco_customer_id: pricingLco.customer_id, package_id, price: n }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Price set: ${n.toFixed(2)}`);
        // Refresh so the pre-fill reflects the just-saved value
        const r2 = await fetch(`${API}/api/iptv/portal/distributor/lco-pricing/${pricingLco.customer_id}`,
                               { headers: auth.headers });
        if (r2.ok) setLcoPricingRows((await r2.json()).pricing || []);
      } else toast.error(d.detail || 'Failed');
    } catch { toast.error('Network error'); }
  };

  const lcoPriceFor = (pkg_id) => lcoPricingRows.find(r => r.package_id === pkg_id)?.price ?? '';

  if (loading || !me) {
    return <div className="text-sm text-slate-500 py-8 text-center">Loading distributor profile…</div>;
  }

  return (
    <div className="space-y-4" data-testid="distributor-self-panel">
      {/* Profile card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><Wallet className="h-4 w-4" /> My IPTV Wallet</div>
            <div className="text-2xl font-bold text-emerald-600" data-testid="distributor-wallet">₹{(me.wallet_balance || 0).toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><Users className="h-4 w-4" /> My LCOs</div>
            <div className="text-2xl font-bold text-indigo-600" data-testid="distributor-lco-count">{me.lco_count || lcos.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><KeyRound className="h-4 w-4" /> ADIGITAL API Key</div>
            <div className="flex items-center gap-2">
              {me.iptv_access_token
                ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Configured (own key)</Badge>
                : <Badge variant="outline" className="text-slate-500">Using Partner&apos;s</Badge>}
              <Button size="sm" variant="outline" onClick={() => { setApiKeyInput(''); setShowApiKey(true); }} data-testid="set-api-key-btn">
                <Pencil className="h-3 w-3 mr-1" /> Set
              </Button>
              {me.iptv_access_token && (
                <Button size="sm" variant="ghost" onClick={clearApiKey}><Trash2 className="h-3 w-3 text-rose-500" /></Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* LCO list */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">My LCOs ({lcos.length})</CardTitle>
            <Button onClick={() => setShowCreateLco(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-lco-btn">
              <Plus className="h-4 w-4 mr-1" /> New LCO
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {lcos.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No LCOs yet — click &quot;New LCO&quot; to onboard your first one.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>LCO</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Wallet</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lcos.map(l => (
                    <TableRow key={l.customer_id} data-testid={`lco-row-${l.customer_id}`}>
                      <TableCell>
                        <div className="font-medium">{l.company_name || l.name}</div>
                        <div className="text-xs text-slate-500">{l.name}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{l.email}</div>
                        <div className="text-xs text-slate-500">{l.phone}</div>
                      </TableCell>
                      <TableCell className="text-sm font-mono">₹{(l.wallet_balance || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openLcoPricing(l)} data-testid={`lco-pricing-btn-${l.customer_id}`}>
                          <Pencil className="h-4 w-4 mr-1" /> Pricing
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Key dialog */}
      <Dialog open={showApiKey} onOpenChange={setShowApiKey}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Your ADIGITAL API Key</DialogTitle>
            <div className="text-xs text-slate-500 mt-1">
              When you set your own key, all activations under your account use it (instead of your Partner&apos;s).
              Leave blank to fall back to your Partner&apos;s key.
            </div>
          </DialogHeader>
          <Input
            value={apiKeyInput}
            onChange={e => setApiKeyInput(e.target.value)}
            placeholder="Paste your ADIGITAL x-access-token"
            data-testid="api-key-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApiKey(false)}>Cancel</Button>
            <Button onClick={saveApiKey} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-api-key-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create LCO dialog */}
      <Dialog open={showCreateLco} onOpenChange={setShowCreateLco}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New LCO under You</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[
              { k: 'name', l: 'Contact Name *', p: 'Ramesh' },
              { k: 'company_name', l: 'Company', p: 'RS Cable' },
              { k: 'email', l: 'Email *', p: 'lco@example.com' },
              { k: 'phone', l: 'Phone *', p: '9876543210' },
              { k: 'address', l: 'Address', p: '' },
            ].map(f => (
              <div key={f.k}>
                <Label>{f.l}</Label>
                <Input value={lcoForm[f.k]} onChange={e => setLcoForm({ ...lcoForm, [f.k]: e.target.value })} placeholder={f.p} data-testid={`create-lco-${f.k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateLco(false)}>Cancel</Button>
            <Button onClick={createLco} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-create-lco">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LCO Pricing dialog */}
      <Dialog open={showLcoPricing} onOpenChange={setShowLcoPricing}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Set Package Prices for {pricingLco?.company_name || pricingLco?.name}</DialogTitle>
            <div className="text-xs text-slate-500 mt-1">
              This is the price your LCO pays you per subscription. Your commission = LCO price − your cost (what your Partner charges you).
            </div>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {(packages || []).map(pkg => (
              <DistributorPricingRow
                key={pkg.package_id}
                pkg={pkg}
                current={lcoPriceFor(pkg.package_id)}
                onSave={(p) => saveLcoPricing(pkg.package_id, p)}
              />
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLcoPricing(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ============================================================
 * Main export — picks the right mode based on `role`
 * ============================================================ */

export default function IPTVDistributorsPanel({ role = 'partner', packages = [] }) {
  return role === 'distributor'
    ? <DistributorSelfPanel packages={packages} />
    : <PartnerDistributorsPanel packages={packages} />;
}
